import SelectInput from "components/Forms/SelectInput";
import useBranchForm from "components/hooks/useBranchForm";
import useBranchLevel from "components/hooks/useBranchLevel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Control, Controller } from "react-hook-form";
import { useBranch } from "UI/BranchContext";
import { BranchValues, FilterType, Options } from "utils/helper/types";

type MultiBranchKey = keyof BranchValues | `${keyof BranchValues}|${keyof BranchValues}` | 
`${keyof BranchValues}|${keyof BranchValues}|${keyof BranchValues}` | 
`${keyof BranchValues}|${keyof BranchValues}|${keyof BranchValues}|${keyof BranchValues}`;

// Defines the props that the `Branch` component can accept.
export type BranchProps = {
  isForm?: boolean;
  isModal ?: boolean;
  formClassName ?: string;
  control?: Control<any>;
  noLabel?: boolean;
  disabled?: Partial<Record<keyof BranchValues, boolean>>; // Is the field required?
  required?: Partial<Record<keyof BranchValues, boolean>>; // Is the field required?
  setter ?: (values: BranchValues, from ?: FilterType, when ?: keyof BranchValues) => void; // A function to set branch-related values.

  // Conditional type: Either `getter` and `getWhen` are defined, or both are undefined.
} & (
  | {
      getter?: undefined; // `getter` is undefined if not used.
      getWhen?: undefined; // `getWhen` is undefined if not used.
    }
  | {
      getter: (values: BranchValues, from ?: FilterType, when ?: keyof BranchValues) => void; // A function to retrieve branch-related values when `getWhen` condition is met.
      getWhen: MultiBranchKey; // Specifies which key in `BranchValues` triggers the `getter` function.
    }
  ) &
  (
    // Specifies different configurations for additional options like year, class, section, etc.
    | {
        addYear: true; // Indicates that an academic year is included.
        addClass?: boolean; // Optional: Indicates whether a class is included.
        addSection?: boolean; // Optional: Indicates whether a section is included.
        addCombinedClassSection?: undefined; // Not included if `addCombinedClassSection` is used.
      }
    | {
        addYear: true; // Academic year is always included.
        addClass?: undefined; // Class is not included if this configuration is used.
        addSection?: undefined; // Section is not included if this configuration is used.
        addCombinedClassSection?: boolean; // Optional: Combines class and section in a single selection.
      }
    | {
      addYear?: boolean; // Optional: Indicates whether an academic year is included.
      addClass?: undefined; // Class is not included.
      addSection?: undefined; // Section is not included.
      addCombinedClassSection?: undefined; // Combined class-section is not included.
    }
  );


function Branch({ isForm, isModal, formClassName, control, noLabel, disabled, required,
  setter, getter, getWhen, addYear, addClass, addSection, addCombinedClassSection
}: BranchProps) {
  const use_role_level = localStorage.getItem("use_role_level");
  const defaultAcademicYear = localStorage.getItem("branchAcademicBatchId");
  const [selectedValues, setSelectedValues] = useState<BranchValues>({
    branchId : "",
    branchAcademicBatchId : "",
    academicClassId : "",
    academicSectionId : ""
  })

  const {defaultBranch, branches} = useBranch();
  const {branchAcademicBatchApi, classSectionsApi, academicClassesApi, academicSectionsApi} = useBranchLevel({branchAcademicBatches : true, 
    classes : true,
    sections : true,
    combinedClassSections : true
  })


      useEffect(() => {
        if(use_role_level === "branch" && defaultBranch && !isForm){
            const newValues = {...selectedValues, branchId : defaultBranch};
            // if(addYear){
            //   branchAcademicBatchApi.action({branchId : defaultBranch});
            // }
            // if(getWhen?.split("|")?.includes("branchId")){
            //   getter(newValues, "branch", "branchId");
            // }
            // if(setter){
            //   setter(newValues, "branch", "branchId");
            // }
            setSelectedValues(newValues);
        }
      }, [use_role_level, defaultBranch, branches, isForm])

      const finalUpdate = useRef(true);
      useEffect(() => {
        if(finalUpdate.current && use_role_level === "branch" && defaultBranch && !isForm && branches?.length && selectedValues.branchId){
          finalUpdate.current = false;
          const isBranchExists = branches.filter(({value}) => value === defaultBranch)?.[0];
          if(isBranchExists){
            const newValues = {...selectedValues, branchId : defaultBranch};
            if(addYear){
              branchAcademicBatchApi.action({branchId : defaultBranch});
            }
            if(getWhen?.split("|")?.includes("branchId")){
              getter(newValues, "branch", "branchId");
            }
            if(setter){
              setter(newValues, "branch", "branchId");
            }
          } else {
            setSelectedValues({branchId : "", branchAcademicBatchId : "", academicClassId : "", academicSectionId : ""})
          }
        }

      }, [use_role_level, defaultBranch, branches, selectedValues.branchId, isForm])
  
      useEffect(() => {
        if(!finalUpdate.current && use_role_level === "branch" &&  defaultAcademicYear && addYear && !isForm){
          const newValues = {...selectedValues, branchId : defaultBranch, branchAcademicBatchId: defaultAcademicYear};
          if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
            getter(newValues, "branch", "branchAcademicBatchId");
          }
          if(setter){
            setter(newValues, "branch", "branchAcademicBatchId");
          }
          setSelectedValues(newValues);
          if(addClass){
            academicClassesApi.action({branchAcademicBatchId : defaultAcademicYear});
          } else if (addCombinedClassSection){
            classSectionsApi.action({branchAcademicBatchId : defaultAcademicYear})
          }
        }
      }, [use_role_level, finalUpdate.current, defaultAcademicYear, addYear, isForm])

      // IS FORM LOGIC
      const firstUpdate = useRef(true);
      const branchFields = useMemo(() => {
        if(isForm){
          const formValues = ["branchId"];
          if(addYear){
            formValues.push("branchAcademicBatchId");
          }
          if(addClass){
            formValues.push("academicClassId");
          }
          if(addSection){
            formValues.push("academicSectionId");
          }
          return formValues as (keyof BranchValues)[];
        } else {
          return [];
        }

      }, [isForm, addYear, addClass, addSection])

      const { formValues, showError, errorMessage } = useBranchForm({control, fields : branchFields })

      useEffect(() => {
        if(firstUpdate.current && 
          (formValues?.branchId || formValues?.branchAcademicBatchId || formValues?.academicClassId || formValues?.academicSectionId) 
          && isForm){
          firstUpdate.current = false;
          if(formValues?.branchId){
            if(addYear){
              branchAcademicBatchApi.action({branchId : formValues.branchId});
            }
          }
          if(formValues?.branchAcademicBatchId){
            if(addClass){
              academicClassesApi.action({branchAcademicBatchId : formValues.branchAcademicBatchId});
            } else if (addCombinedClassSection){
              classSectionsApi.action({branchAcademicBatchId : formValues.branchAcademicBatchId})
            }
          }
          if(formValues?.academicClassId){
            if(addSection){
              academicSectionsApi.action({academicClassId : formValues.academicClassId});
            }
          }
        }
      }, [isForm, formValues])
      // IS FORM LOGIC END

     const branchAcademicBatches : Options<string> = useMemo(() => {
          return (branchAcademicBatchApi.data ?? []).map((year) => {
            return {
              label: year.name,
              value: year._id,
            };
          });
      }, [branchAcademicBatchApi?.data]);
  
      const academicClasses: Options = useMemo(() => {
        if (!academicClassesApi?.data) return [];
        return academicClassesApi?.data?.map((item: any) => {
          return {
            value: item._id,
            label: item.branchClass?.[0]?.name,
          };
        });
      }, [academicClassesApi?.data]);
  
      const academicSections: Options = useMemo(() => {
        if (!academicSectionsApi?.data) return [];
        return academicSectionsApi?.data?.map((item: any) => {
          return {
            value: item._id,
            label: item.section?.[0]?.sectionName,
          };
        });
      }, [academicSectionsApi?.data]);
    
      const classSections: Options = useMemo(() => {
        return (classSectionsApi?.data ?? []).map((cls) => {
          return {
            label: cls?.className + "-" + cls?.sectionName,
            value: `${cls?.academicClassId}:-:${cls.academicSectionId}`,
          };
        });
      }, [classSectionsApi?.data]);

  if (isForm) {
    return (
      <>
        <Form.Group controlId="branchId" className={formClassName}><Controller
          control={control}
          name="branchId"
          defaultValue={""}
          render={({ field }) => {
            return (
              <SelectInput
                {...field}
                label={noLabel ? "" : "Branch"}
                disabled={!!disabled?.["branchId"]}
                required={!!required?.["branchId"]}
                showError={!!showError?.["branchId"]}
                validationMessage={errorMessage?.["branchId"]}
                onChange={async (e) => {
                  firstUpdate.current = false;
                  field.onChange(e);
                  const newValues = {...selectedValues, branchId : e.target.value, branchAcademicBatchId : '', academicClassId : '', academicSectionId : ''} as BranchValues
                  let currentYear = ""
                  
                  if(e.target.value && addYear){
                    await branchAcademicBatchApi.action({branchId : e.target.value});
                    // currentYear = data.filter(({isCurrentAcademicYear}) => !!isCurrentAcademicYear)?.[0]?._id || ""
                    // newValues.branchAcademicBatchId = currentYear;
                    
                    // if(currentYear){
                    //   if(addClass){
                    //     academicClassesApi.action({branchAcademicBatchId : newValues.branchAcademicBatchId});
                    //   } else if (addCombinedClassSection){
                    //     classSectionsApi.action({branchAcademicBatchId : newValues.branchAcademicBatchId})
                    //   }

                    //   if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
                    //     getter(newValues, "branch", "branchAcademicBatchId")
                    //   }
                    // }
                  } else if(addYear) {
                    branchAcademicBatchApi.clearActions.clearData();
                  }
                  if(addClass) {
                    academicClassesApi.clearActions.clearData();
                  }
                  if(addSection) {
                    academicSectionsApi.clearActions.clearData();
                  }
                  if(addCombinedClassSection) {
                    classSectionsApi.clearActions.clearData();
                  }
                  
                  if(getWhen?.split("|")?.includes("branchId")) {
                    getter(newValues, "branch", "branchId")
                  }

                  if(currentYear){
                    if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
                      getter(newValues, "branch", "branchAcademicBatchId")
                    }
                    if(setter){
                      setter(newValues, "branch", "branchAcademicBatchId")
                    }
                  }
                }}
                placeholder="Branch"
                className={`${isModal ? "theme-input-control-modal" : "theme-input-control"}`}
                options={branches}
              />
            );
          }}
        /></Form.Group>
        {addYear && <Form.Group controlId="branchAcademicBatchId" className={formClassName}><Controller
          control={control}
          name="branchAcademicBatchId"
          defaultValue={""}
          render={({ field }) => {
            return (
              <SelectInput
                {...field}
                label={noLabel ? "" : "Academic Year"}
                disabled={!!disabled?.["branchAcademicBatchId"]}
                required={!!required?.["branchAcademicBatchId"]}
                showError={!!showError?.["branchAcademicBatchId"]}
                validationMessage={errorMessage?.["branchAcademicBatchId"]}
                onChange={async (e) => {
                  field.onChange(e);
                  const newValues = {...selectedValues, branchAcademicBatchId : e.target.value, academicClassId : '', academicSectionId : ''} as BranchValues
            
                  if(e.target.value){
                    if(addClass){
                      academicClassesApi.action({branchAcademicBatchId : e.target.value});
                    }
                    else if(addCombinedClassSection){
                      classSectionsApi.action({branchAcademicBatchId : e.target.value});
                    }
                  } else if(addClass) {
                    academicClassesApi.clearActions.clearData();
                  }
                  else if(addCombinedClassSection) {
                    classSectionsApi.clearActions.clearData();
                  }
    
                  if(addSection) {
                    academicSectionsApi.clearActions.clearData();
                  }
                  
                  if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
                    getter(newValues, "branch", "branchAcademicBatchId")
                  }
                }}
                placeholder="Academic Year"
                className={`${isModal ? "theme-input-control-modal" : "theme-input-control"}`}
                options={branchAcademicBatches}
              />
            );
          }}
        /></Form.Group>}
        {addClass && <Form.Group controlId="academicClassId" className={formClassName}><Controller
          control={control}
          name="academicClassId"
          defaultValue={""}
          render={({ field }) => {
            return (
              <SelectInput
                {...field}
                label={noLabel ? "" : "Class"}
                disabled={!!disabled?.["academicClassId"]}
                required={!!required?.["academicClassId"]}
                showError={!!showError?.["academicClassId"]}
                validationMessage={errorMessage?.["academicClassId"]}
                onChange={async (e) => {
                  field.onChange(e);
                  const newValues = {...selectedValues, academicClassId : e.target.value, academicSectionId : ''} as BranchValues
            
                  if(e.target.value && addSection) {
                    academicSectionsApi.action({academicClassId : e.target.value});
                  }
                  else if(addSection) {
                    academicSectionsApi.clearActions.clearData();
                  }
                  
                  if(getWhen?.split("|")?.includes("academicClassId")) {
                    getter(newValues, "branch", "academicClassId")
                  }
                }}
                placeholder="Class"
                className={`${isModal ? "theme-input-control-modal" : "theme-input-control"}`}
                options={academicClasses}
              />
            );
          }}
        /></Form.Group>}
        {addSection && <Form.Group controlId="academicSectionId" className={formClassName}><Controller
          control={control}
          name="academicSectionId"
          defaultValue={""}
          render={({ field }) => {
            return (
              <SelectInput
                {...field}
                label={noLabel ? "" : "Section"}
                disabled={!!disabled?.["academicSectionId"]}
                required={!!required?.["academicSectionId"]}
                showError={!!showError?.["academicSectionId"]}
                validationMessage={errorMessage?.["academicSectionId"]}
                onChange={async (e) => {
                  field.onChange(e);
                  const newValues = {...selectedValues, academicSectionId : e.target.value} as BranchValues
                  
                  if(getWhen?.split("|")?.includes("academicSectionId")) {
                    getter(newValues, "branch", "academicSectionId")
                  }
                }}
                placeholder="Section"
                className={`${isModal ? "theme-input-control-modal" : "theme-input-control"}`}
                options={academicSections}
              />
            );
          }}
        /></Form.Group>}
      </>
    );
  } else {
    return (<>
      <SelectInput
          placeholder="Branch"
          value={selectedValues.branchId}
          className="theme-input-control width-filter"
          onChange={async (e) => {
              const newValues = {...selectedValues, branchId : e.target.value, branchAcademicBatchId : '', academicClassId : '', academicSectionId : ''} as BranchValues
              let currentYear = ""
              if(e.target.value && addYear){
                const data = await branchAcademicBatchApi.action({branchId : e.target.value});
                currentYear = data.filter(({isCurrentAcademicYear}) => !!isCurrentAcademicYear)?.[0]?._id || ""
                newValues.branchAcademicBatchId = currentYear;
                
                if(currentYear){
                  if(addClass){
                    academicClassesApi.action({branchAcademicBatchId : newValues.branchAcademicBatchId});
                  } else if (addCombinedClassSection){
                    classSectionsApi.action({branchAcademicBatchId : newValues.branchAcademicBatchId})
                  }
                }
              } else if (addYear) {
                branchAcademicBatchApi.clearActions.clearData();
              }

              if(addClass) {
                academicClassesApi.clearActions.clearData();
              }
              if(addSection) {
                academicSectionsApi.clearActions.clearData();
              }
              if(addCombinedClassSection) {
                classSectionsApi.clearActions.clearData();
              }
                              
              if(getWhen?.split("|")?.includes("branchId")) {
                  getter(newValues, "branch", "branchId")
              }
              if(setter){
                setter(newValues, "branch", "branchId")
              }

              if(currentYear){
                if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
                  getter(newValues, "branch", "branchAcademicBatchId")
                }
                if(setter){
                  setter(newValues, "branch", "branchAcademicBatchId")
                }
              }

              setSelectedValues(newValues)
          }}
          options={branches}
      />
      {addYear && <SelectInput
          placeholder="Academic Year"
          value={selectedValues.branchAcademicBatchId}
          className="theme-input-control width-filter"
          onChange={(e) => {
              const newValues = {...selectedValues, branchAcademicBatchId : e.target.value, academicClassId : '', academicSectionId : ''} as BranchValues
              
              if(e.target.value){
                if(addClass){
                  academicClassesApi.action({branchAcademicBatchId : e.target.value});
                }
                else if(addCombinedClassSection){
                  classSectionsApi.action({branchAcademicBatchId : e.target.value});
                }
              } else if(addClass) {
                academicClassesApi.clearActions.clearData();
              }
              else if(addCombinedClassSection) {
                classSectionsApi.clearActions.clearData();
              }

              if(addSection) {
                academicSectionsApi.clearActions.clearData();
              }
              
              if(getWhen?.split("|")?.includes("branchAcademicBatchId")) {
                  getter(newValues, "branch", "branchAcademicBatchId")
              }
              if(setter){
                setter(newValues, "branch", "branchAcademicBatchId")
              }
              setSelectedValues(newValues)
          }}
          options={branchAcademicBatches}
      />}
      {addClass && <SelectInput
          placeholder="Class"
          value={selectedValues.academicClassId}
          className="theme-input-control width-filter"
          onChange={(e) => {
              const newValues = {...selectedValues, academicClassId : e.target.value, academicSectionId : ''} as BranchValues
              
              if(e.target.value) {
                academicSectionsApi.action({academicClassId : e.target.value});
              }
              else if(addSection) {
                academicSectionsApi.clearActions.clearData();
              }

              if(getWhen?.split("|")?.includes("academicClassId")) {
                  getter(newValues, "branch", "academicClassId")
              }
              if(setter){
                setter(newValues, "branch", "academicClassId")
              }
              setSelectedValues(newValues)
          }}
          options={academicClasses}
      />}
      {addSection && <SelectInput
          placeholder="Section"
          value={selectedValues.academicSectionId}
          className="theme-input-control width-filter"
          onChange={(e) => {
              const newValues = {...selectedValues, academicSectionId : e.target.value} as BranchValues
              
              if(getWhen?.split("|")?.includes("academicSectionId")){
                  getter(newValues, "branch", "academicSectionId")
              }
              if(setter){
                setter(newValues, "branch", "academicSectionId")
              }
              setSelectedValues(newValues)
          }}
          options={academicSections}
      />}
      {addCombinedClassSection && <SelectInput
          placeholder="Class & Section"
          value={selectedValues.academicClassId + ":-:" + selectedValues.academicSectionId}
          className="theme-input-control width-filter"
          onChange={(e) => {
              const [academicClassId, academicSectionId] = e.target.value.split(":-:")
              const newValues = {...selectedValues, academicClassId, academicSectionId } as BranchValues
              
              if(getWhen?.split("|")?.includes("academicClassId") || getWhen?.split("|")?.includes("academicSectionId")){
                  getter(newValues, "branch", "academicClassId")
              }
              if(setter){
                setter(newValues, "branch", "academicClassId")
              }
              setSelectedValues(newValues)
          }}
          options={classSections}
      />}
  </>);
  }
}

export default Branch;
